import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const VotingPage = () => {
  const navigate = useNavigate();
  const { token, user, refreshUser } = useAuth();
  const [participants, setParticipants] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      fetchParticipants(selectedEvent);
    }
  }, [selectedEvent]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      const ongoingEvents = response.data.filter(e => e.status === "ongoing");
      setEvents(ongoingEvents);
      if (ongoingEvents.length > 0) {
        setSelectedEvent(ongoingEvents[0].id);
      }
    } catch (error) {
      toast.error("Gagal memuat event");
    } finally {
      setLoading(false);
    }
  };

  const fetchParticipants = async (eventId) => {
    try {
      const response = await axios.get(`${API}/participants/${eventId}`);
      setParticipants(response.data);
    } catch (error) {
      toast.error("Gagal memuat peserta");
    }
  };

  const handleVote = async () => {
    if (!selectedParticipant) return;

    if ((user?.tokens_balance || 0) < 2) {
      toast.error("Token tidak cukup! Dibutuhkan 2 token untuk 1 vote");
      return;
    }

    setVoting(true);
    try {
      const response = await axios.post(
        `${API}/votes`,
        {
          participant_id: selectedParticipant.id,
          participant_name: selectedParticipant.name,
          event_id: selectedEvent,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      await refreshUser();
      setSelectedParticipant(null);
      fetchParticipants(selectedEvent);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal melakukan vote");
    } finally {
      setVoting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-gradient-ltb-r text-white p-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center">
            <button onClick={() => navigate("/home")} className="mr-4">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-xl font-bold">Voting Peserta</h1>
          </div>
          <div className="bg-white/20 px-4 py-2 rounded-lg">
            <p className="text-xs">Token Saya</p>
            <p className="text-lg font-bold">{user?.tokens_balance || 0}</p>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4">
        {/* Event Selector */}
        {events.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Pilih Event:</label>
            <select
              value={selectedEvent}
              onChange={(e) => setSelectedEvent(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {events.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Info Card */}
        <div className="bg-gradient-to-r from-green-500 to-teal-500 rounded-xl shadow-lg p-6 mb-6 text-white">
          <div className="flex items-center space-x-4">
            <div className="bg-white/20 rounded-full p-3">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold">1 Vote = 2 Token</h3>
              <p className="text-sm opacity-90">Vote pemain favorit Anda sekarang!</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-500">Memuat...</div>
          </div>
        ) : events.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Belum Ada Event Aktif</h3>
            <p className="text-gray-500">Voting akan dibuka saat ada event yang sedang berlangsung</p>
          </div>
        ) : participants.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Belum Ada Peserta</h3>
            <p className="text-gray-500">Peserta akan ditampilkan di sini</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {participants.map((participant) => (
              <div
                key={participant.id}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                {participant.photo_url ? (
                  <img
                    src={participant.photo_url}
                    alt={participant.name}
                    className="w-full h-48 object-cover"
                  />
                ) : (
                  <div className="w-full h-48 bg-gradient-ltb flex items-center justify-center">
                    <svg className="w-20 h-20 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
                <div className="p-4">
                  <h3 className="text-lg font-bold text-gray-800 mb-1">{participant.name}</h3>
                  <p className="text-sm text-gray-600 mb-3">{participant.team_name}</p>
                  
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      <span className="text-lg font-bold text-gray-800">{participant.total_votes}</span>
                    </div>
                    <span className="text-xs text-gray-500">votes</span>
                  </div>

                  <button
                    onClick={() => setSelectedParticipant(participant)}
                    className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-2 rounded-lg transition-colors"
                  >
                    Vote (2 Token)
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Vote Confirmation Dialog */}
      {selectedParticipant && (
        <Dialog open={!!selectedParticipant} onOpenChange={() => setSelectedParticipant(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Konfirmasi Vote</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="text-center">
                {selectedParticipant.photo_url ? (
                  <img
                    src={selectedParticipant.photo_url}
                    alt={selectedParticipant.name}
                    className="w-32 h-32 rounded-full mx-auto mb-4 object-cover"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-full bg-gradient-ltb mx-auto mb-4 flex items-center justify-center">
                    <svg className="w-16 h-16 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
                <h3 className="text-xl font-bold text-gray-800 mb-1">{selectedParticipant.name}</h3>
                <p className="text-gray-600">{selectedParticipant.team_name}</p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Biaya vote:</span>
                  <span className="text-xl font-bold text-yellow-600">2 Token</span>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-gray-700">Token Anda:</span>
                  <span className="text-xl font-bold text-gray-800">{user?.tokens_balance || 0} Token</span>
                </div>
              </div>

              <Button
                onClick={handleVote}
                disabled={voting || (user?.tokens_balance || 0) < 2}
                className="w-full"
              >
                {voting ? "Memproses..." : "Konfirmasi Vote"}
              </Button>

              {(user?.tokens_balance || 0) < 2 && (
                <p className="text-center text-sm text-red-600">
                  Token tidak cukup.{" "}
                  <button
                    onClick={() => {
                      setSelectedParticipant(null);
                      navigate("/tokens/purchase");
                    }}
                    className="underline font-semibold"
                  >
                    Beli token
                  </button>
                </p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default VotingPage;
